import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { type EventHit, renderEventHit, InlineSearchResults } from "@/components/AlgoliaSearch";
import { showSnackbar } from "@/lib/snackbarUtils";
import EmailPreviewDialog, {
  EMAIL_PREVIEW_MODAL_CLASSNAME,
  EmailPreviewModalActions
} from "@/components/EmailPreviewDialog";
import { useTransferredEventRegistrationEmailPreview } from "@/pages/events/_hooks/useTransferredEventRegistrationEmailPreview";
import { TRANSFER_ELIGIBLE_EVENTS_QUERY, TRANSFER_EVENT_REGISTRATION_MUTATION } from "../../../queries";
import type { TransferEventSearchNode, TransferEligibleEventsResult } from "../../../types";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import TextField from "@/components/TextField";
import Icon from "@/components/Icon";
import { useEventsBasePath } from "@/pages/events/_eventsBasePathContext";
import SubSectionHeading from "@/components/SubSectionHeading/SubSectionHeading";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

const TRANSFER_SEARCH_PAGE_SIZE = 20;

interface TransferRegistrationModalProps {
  registrationId: string;
  registrantName: string;
  registrantPgaId: string;
  currentEventId: string;
  onClosed: () => void;
  refetch: () => void;
}

const mapEventToHit = (event: TransferEventSearchNode): EventHit => ({
  objectID: event.id,
  id: event.id,
  title: event.title,
  slug: event.slug,
  startsAt: event.startsAt,
  endsAt: event.endsAt,
  city: event.city,
  state: event.state,
  eventCode: event.eventCode,
  facilityName: event.facility?.name
});

export default function TransferRegistrationModal({
  registrationId,
  registrantName,
  registrantPgaId,
  currentEventId,
  onClosed,
  refetch
}: TransferRegistrationModalProps) {
  const navigate = useNavigate();
  const basePath = useEventsBasePath();
  const { eventSlug = "" } = useParams();
  const { trackEvent } = useAmplitude();

  const [query, setQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedEvent, setSelectedEvent] = useState<TransferEventSearchNode | null>(null);
  const [bannerError, setBannerError] = useState("");
  const searchBoxRef = useRef<HTMLDivElement>(null);

  const { data, loading: searchLoading } = useQuery<TransferEligibleEventsResult>(TRANSFER_ELIGIBLE_EVENTS_QUERY, {
    variables: {
      eventId: currentEventId,
      pgaId: registrantPgaId,
      first: TRANSFER_SEARCH_PAGE_SIZE,
      search: debouncedSearch || undefined
    }
  });

  const [transferRegistration, { loading: mutationLoading }] = useMutation(TRANSFER_EVENT_REGISTRATION_MUTATION);
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = useTransferredEventRegistrationEmailPreview();

  useEffect(() => () => resetPreview(), [resetPreview]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(query);
    }, 300);
    return () => clearTimeout(handler);
  }, [query]);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      const textField =
        searchBoxRef.current?.querySelector<HTMLElement>("md-outlined-text-field") ||
        searchBoxRef.current?.querySelector<HTMLElement>("md-filled-text-field");
      textField?.focus();
    });
  }, []);

  useEffect(() => {
    focusInput();
  }, [focusInput]);

  const eventsArray = useMemo(
    () => data?.eligibleEvents?.edges?.map(({ node }) => node).filter((event) => event.id !== currentEventId) ?? [],
    [data, currentEventId]
  );

  const handleClear = () => {
    setQuery("");
    setSelectedEvent(null);
    setBannerError("");
    focusInput();
  };

  const handleSelectEvent = (event: TransferEventSearchNode) => {
    setSelectedEvent(event);
    setQuery(event.title);
    setBannerError("");
  };

  const onTransferSuccess = (destSlug: string) => {
    refetch();
    onClosed();
    showSnackbar("Registration transferred", {
      actions: [
        <Button key="view-event" variant="text" onClick={() => navigate(`${basePath}/${destSlug}/registrations`)}>
          View Event
        </Button>
      ],
      close: true
    });
    trackEvent(
      EVENT_ANALYTICS_EVENTS.registrationTransferred,
      getEventAnalyticsData({
        source: "registrations",
        eventId: currentEventId,
        eventSlug,
        registrationId,
        action: "transfer"
      })
    );
  };

  const handleTransfer = async () => {
    if (!selectedEvent) return;

    try {
      const { data: result } = await transferRegistration({
        variables: {
          input: {
            sourceRegistrationId: registrationId,
            targetEventId: selectedEvent.id
          }
        }
      });

      if (!result?.transferEventRegistration?.success) {
        setBannerError(result?.transferEventRegistration?.message || "An unexpected error occurred.");
        return;
      }

      onTransferSuccess(selectedEvent.slug);
    } catch (error) {
      setBannerError(error instanceof Error ? error.message : "An unexpected error occurred.");
    }
  };

  const trailingIcon = query ? (
    <button className="cursor-pointer flex items-center h-full" onClick={handleClear} aria-label="Clear search query">
      <Icon name="cancel" />
    </button>
  ) : undefined;

  const handlePreviewEmail = () => {
    if (!selectedEvent) return;

    setPreviewOpen(true);
    void loadPreview({
      sourceRegistrationId: registrationId,
      targetEventId: selectedEvent.id
    });
  };

  return (
    <>
      <Dialog
        open
        onClosed={onClosed}
        headline="Transfer Registration"
        data-testid="transfer-registration-modal"
        className={`${EMAIL_PREVIEW_MODAL_CLASSNAME} max-w-[560px] h-[80vh] max-h-[90vh] p-6 pr-2`}
        hasFormElements
        actions={
          <EmailPreviewModalActions onPreview={handlePreviewEmail} previewDisabled={!selectedEvent || mutationLoading}>
            <Button variant="text" onClick={onClosed}>
              Cancel
            </Button>
            <Button disabled={mutationLoading || !selectedEvent} onClick={handleTransfer}>
              Transfer
            </Button>
          </EmailPreviewModalActions>
        }
      >
        {!!bannerError && (
          <div className="mb-6">
            <Banner inline variant="error" description="The transfer could not be completed." subText={bannerError} />
          </div>
        )}

        <p className="text-body-medium mb-6">
          Select an available event to transfer this registration to. The registrant will be notified via email of the
          transfer:
        </p>
        <div ref={searchBoxRef}>
          <TextField
            label="Destination Event"
            value={query}
            onChangeText={(value) => {
              setQuery(value);
              setSelectedEvent(null);
              setBannerError("");
            }}
            leadingIcon="search"
            trailingIcon={trailingIcon}
            variant="outlined"
            className="w-full"
            data-testid="transfer-event-search-input"
          />
        </div>

        {!selectedEvent && (
          <InlineSearchResults
            results={eventsArray}
            isLoading={searchLoading}
            getKey={(event) => event.id}
            renderItem={(event) => renderEventHit(mapEventToHit(event), () => handleSelectEvent(event))}
            noResultsMessage={query.trim() ? "No events found" : "No eligible events available"}
            className="mt-4"
          />
        )}

        {selectedEvent && (
          <div className="mt-6">
            <SubSectionHeading>Transfer {registrantName} registration to:</SubSectionHeading>
            <div className="mt-2 rounded-lg p-2 transition-colors hover:bg-sys-brand-surface-container-high">
              {renderEventHit(mapEventToHit(selectedEvent), handleClear)}
            </div>
          </div>
        )}
      </Dialog>
      <EmailPreviewDialog
        open={previewOpen}
        onClosed={() => setPreviewOpen(false)}
        subject={previewLoading ? undefined : preview?.subject}
        html={previewLoading ? undefined : preview?.html}
        loading={previewLoading}
        errorMessage={previewLoading ? null : (previewError?.message ?? null)}
      />
    </>
  );
}
